/**
 * Where a notification goes when someone taps it, decided by what it IS.
 *
 * The bell used to decide by which related id happened to be present, in a
 * fixed order: thread, then job, then quote. That reads as a reasonable
 * fallback chain and is wrong for the one type that carries all three. A
 * `quote_accepted` row names a job, a quote AND the thread the acceptance
 * created, so the thread won and every accepted quote opened the chat — which
 * is what Daniel reported on 2026-09-09 as "no navega al detalle".
 *
 * So the type decides, and the related ids are only the parameters it needs.
 * The table below is the mirror of `_TYPE_TO_PATH` in backend/services/notify.py
 * — the same destination the push carries for the same row, so the bell and the
 * device never disagree about where one notification leads. Keep them in step.
 *
 * The id-order chain survives underneath as the answer for a type neither side
 * has an entry for, because landing on a related page beats landing on "#".
 */

const chatBase = (role) =>
  role === "provider" ? "/provider/messages" : "/client/messages";

const jobsBase = (role) =>
  role === "provider" ? "/provider/jobs" : "/client/jobs";

/**
 * @param {object} n     a notification row from GET /notifications
 * @param {string} role  the READER's role — `provider` or anything else
 * @returns {string} an in-app path
 */
export function notificationLink(n, role) {
  const { type, related_thread_id: thread, related_job_id: job } = n || {};

  switch (type) {
    case "new_message":
      // The thread, named. `/chat` renders the same page but sits outside the
      // dashboard's navigation, so the sidebar highlights nothing there.
      if (thread) return `${chatBase(role)}?thread=${thread}`;
      break;
    case "quote_accepted":
      // The job that was won. Not the thread: opening the chat is a different
      // errand, and the backend stopped answering this one with the thread list
      // in the same change (backend/routes/quotes.py).
      if (job) return `/provider/jobs/${job}`;
      return "/provider/quotes";
    case "quote_sent":
      // The requester's own job page, where the quote is waiting to be read.
      // Role-aware because since DY2.6 a FixyBee can be the requester.
      if (job) return `${jobsBase(role)}/${job}`;
      break;
    case "quote_declined":
      return "/provider/quotes";
    case "job_canceled":
      // T19 X1. The job they were hired for, which they can still read — with
      // the reason on it and the conversation closed.
      if (job) return `/provider/jobs/${job}`;
      return "/provider/quotes";
    case "job_deleted":
      // T19. The job was deleted and its page answers 404; the FixyBee's own
      // quotes list is where what became of their quote is still written.
      return "/provider/quotes";
    case "quote_requested":
      // A job sent to this FixyBee by name. Same destination the push carries.
      if (job) return `/provider/jobs/${job}`;
      return "/provider/dashboard";
    case "job_completed":
      // The job, on the reader's own side. For the requester that page is also
      // where the review modal opens, which is what the notification asks for.
      if (job) return `${jobsBase(role)}/${job}`;
      return role === "provider" ? "/provider/dashboard" : "/client/dashboard";
    case "terminal_payment_received":
      // /provider/terminal is unrouted (PLANES): FixyTerminal is on no
      // surface. A notification from before lands on the dashboard.
      return "/provider/dashboard";
    case "verification_submitted":
    case "verification_approved":
    case "verification_rejected":
    case "verification_requires_input":
      return "/provider/verification";
    case "fixymatch_boost":
    case "fixymatch_new_opportunity":
      return job ? `/provider/jobs/${job}` : "/provider/browse";
    case "subscription_active":
    case "credits_purchased":
      return "/provider/billing";
    case "spotlight_awarded":
    case "new_review":
      // No provider-side reviews view exists — the rating is a KPI tile on the
      // dashboard and Spotlight renders on the public Landing. `spotlight_winner`
      // and `review_received` used to be listed here and were never emitted by
      // anything; the types are the two above.
      return "/provider/dashboard";
    case "support_ticket":
      return "/admin/tickets";
    case "broadcast":
      // The only type whose destination is chosen per notification rather than
      // per type: an admin picks it when composing, so there is no entry any
      // table here could hold. It travels in `params.path`, already resolved
      // for this reader's namespace by the backend.
      if (n?.params?.path) return n.params.path;
      return role === "provider" ? "/provider/dashboard" : "/client/dashboard";
    default:
      break;
  }

  if (thread) return `${chatBase(role)}?thread=${thread}`;
  if (job) return `${jobsBase(role)}/${job}`;
  if (n?.related_quote_id) {
    return role === "provider" ? "/provider/quotes" : "/client/jobs";
  }
  return role === "provider" ? "/provider/dashboard" : "/client/dashboard";
}

export default notificationLink;
